import React, { useState, useEffect } from 'react';
import { usePreferencesStore } from '../../store/preferencesStore';
import { getAccentPresets, normalizeAccentHex } from '../../utils/accentColor';
import { Toggle, Segmented, Row, Section } from '../settingsPrimitives';
import YourToolsSection from '../../onboarding/YourToolsSection';

export const PRESET_LABEL_KEY = {
  purple: 'settings.accentPresetPurple',
  blue:   'settings.accentPresetBlue',
  green:  'settings.accentPresetGreen',
  teal:   'settings.accentPresetTeal',
  orange: 'settings.accentPresetOrange',
  red:    'settings.accentPresetRed',
  pink:   'settings.accentPresetPink',
  white:  'White',
  dark:   'Dark',
};

export const AppearanceTab = ({ t }) => {
  const theme       = usePreferencesStore((s) => s.theme);
  const accentColor = usePreferencesStore((s) => s.accentColor);
  const accentFg    = usePreferencesStore((s) => s.accentFg);
  const density     = usePreferencesStore((s) => s.density);
  const sidebarPosition = usePreferencesStore((s) => s.sidebarPosition);
  const uiFontSize  = usePreferencesStore((s) => s.uiFontSize);
  const showNavTooltips = usePreferencesStore((s) => s.showNavTooltips);
  const workspaceTransition = usePreferencesStore((s) => s.workspaceTransition);
  const setTheme    = usePreferencesStore((s) => s.setTheme);
  const setAccentColor = usePreferencesStore((s) => s.setAccentColor);
  const setWorkspaceTransition = usePreferencesStore((s) => s.setWorkspaceTransition);
  const update      = usePreferencesStore((s) => s.updatePreference);

  const [customHex, setCustomHex] = useState(accentColor);
  const allPresets = getAccentPresets();
  const isDark     = document.documentElement.classList.contains('dark');
  const presets    = allPresets.filter(p => isDark ? p.id !== 'dark' : p.id !== 'white');

  useEffect(() => { setCustomHex(accentColor); }, [accentColor]);

  const handleHexBlur = () => {
    const normalized = normalizeAccentHex(customHex);
    if (normalized) setAccentColor(normalized);
    else setCustomHex(accentColor);
  };

  return (
    <>
      <Section title={t('settings.theme')}>
        <Row label={t('settings.colorScheme')}>
          <Segmented
            value={theme}
            onChange={setTheme}
            options={[
              { id: 'system', label: t('settings.themeSystem') },
              { id: 'light',  label: t('settings.lightMode') },
              { id: 'dark',   label: t('settings.darkMode') },
            ]}
          />
        </Row>
        <Row label={t('settings.accentColor')} desc={t('settings.accentCustom') + ' — override with any hex color.'}>
          <div className="flex flex-col gap-3 items-end">
            <div className="settings-accent-presets">
              {presets.map((preset) => (
                <button
                  key={preset.id}
                  type="button"
                  title={t(PRESET_LABEL_KEY[preset.id])}
                  onClick={() => setAccentColor(preset.hex)}
                  className={`settings-accent-swatch${accentColor === preset.hex ? ' settings-accent-swatch--active' : ''}${preset.id === 'white' || preset.id === 'dark' ? ' settings-accent-swatch--contrast' : ''}`}
                  style={{ backgroundColor: preset.hex }}
                  aria-label={t(PRESET_LABEL_KEY[preset.id])}
                />
              ))}
            </div>
          </div>
        </Row>
        <Row label={t('settings.accentCustom')} desc="Override with any hex color.">
          <div className="settings-hex-wrap">
            <div
              className="settings-hex-swatch"
              style={{ background: normalizeAccentHex(accentColor) || '#8869eb', position: 'relative', cursor: 'pointer' }}
            >
              <input
                type="color"
                value={normalizeAccentHex(customHex) || normalizeAccentHex(accentColor) || '#8869eb'}
                onChange={(e) => {
                  setCustomHex(e.target.value);
                  setAccentColor(e.target.value);
                }}
                style={{ opacity: 0, position: 'absolute', inset: 0, width: '100%', height: '100%', cursor: 'pointer', padding: 0, border: 'none' }}
                aria-label={t('settings.accentHexPlaceholder')}
              />
            </div>
            <input
              type="text"
              value={customHex}
              onChange={(e) => setCustomHex(e.target.value)}
              onBlur={handleHexBlur}
              onKeyDown={(e) => { if (e.key === 'Enter') handleHexBlur(); }}
              placeholder={t('settings.accentHexPlaceholder')}
              className="settings-hex-field"
              spellCheck={false}
              aria-label={t('settings.accentHexPlaceholder')}
            />
          </div>
        </Row>
        <Row label="Text on accent" desc="Color of text, icons and checkmarks drawn on top of accent-colored elements.">
          <Segmented
            value={accentFg}
            onChange={(v) => update('accentFg', v)}
            options={[
              { id: 'auto',  label: 'Auto' },
              { id: 'light', label: 'Light' },
              { id: 'dark',  label: 'Dark' },
            ]}
          />
        </Row>
      </Section>

      <Section title={t('settings.densityLayout')}>
        <Row label={t('settings.density')} desc={t('settings.densityDesc')}>
          <Segmented
            value={density}
            onChange={(v) => update('density', v)}
            options={[
              { id: 'compact',  label: t('settings.densityCompact') },
              { id: 'balanced', label: t('settings.densityBalanced') },
              { id: 'spacious', label: t('settings.densitySpacious') },
            ]}
          />
        </Row>
        <Row label={t('settings.sidebarPosition')}>
          <Segmented
            value={sidebarPosition}
            onChange={(v) => update('sidebarPosition', v)}
            options={[
              { id: 'left',  label: t('settings.sidebarLeft') },
              { id: 'right', label: t('settings.sidebarRight') },
            ]}
          />
        </Row>
        <Row label={t('settings.uiFontSize')}>
          <div className="flex items-center gap-2">
            <input
              type="range"
              min={10}
              max={20}
              value={uiFontSize}
              onChange={(e) => update('uiFontSize', Number(e.target.value))}
              className="settings-slider"
            />
            <span className="settings-unit-label" style={{ minWidth: 32 }}>{uiFontSize}px</span>
          </div>
        </Row>
        <Row label={t('settings.showNavTooltips')} desc={t('settings.showNavTooltipsDesc')}>
          <Toggle value={showNavTooltips} onChange={(v) => update('showNavTooltips', v)} />
        </Row>
      </Section>

      <Section title={t('settings.effects')}>
        <Row label={t('settings.workspaceTransition')} desc={t('settings.workspaceTransitionDesc')}>
          <Toggle value={workspaceTransition} onChange={setWorkspaceTransition} />
        </Row>
      </Section>

      <YourToolsSection />
    </>
  );
};
